import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Grid, makeStyles, Typography } from '@material-ui/core';
import { Link, useLocation } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { Button } from '@swagup-com/components';
import CategoriesSidebar from './CategoriesSidebar';
import Category from './Category';
import Loader from '../../global/Loader';
import styles from './Catalog.styles';

const useStyles = makeStyles(styles);

const useFragment = () => {
  const location = useLocation();
  return location.hash.slice(1);
};

const Catalog = ({ nextStep, isBulk }) => {
  const classes = useStyles();
  const categoryName = useFragment();

  const { items: catalog, loading } = useSelector(state => state.catalog);
  const cartLoading = useSelector(state => state.cart.loading);

  const [selectedCategoryId, setSelectedCategoryId] = useState();

  const refs = useMemo(() => catalog?.map(() => React.createRef()), [catalog]);

  const handleSelectedCategory = useCallback(
    categoryIdx => {
      if (!catalog?.[categoryIdx]) return;
      refs[categoryIdx].current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [catalog, refs]
  );

  const categories = useMemo(
    () => (
      <Grid container direction="column" alignItems="stretch">
        {catalog?.map((category, idx) => (
          <Grid item key={category.id} className={classes.categoryContainer}>
            <div id={category.CatName} className="category-scroll-padding" ref={refs[idx]} />
            <Category category={category} />
          </Grid>
        ))}
      </Grid>
    ),
    [catalog, refs, classes]
  );

  useEffect(() => {
    if (!isEmpty(catalog)) setSelectedCategoryId(catalog[0].id);
  }, [catalog]);

  useEffect(() => {
    if (isEmpty(catalog)) return () => {};

    const handleScroll = () => {
      const idx = refs.findIndex(({ current }) => current?.parentNode.offsetTop >= window.scrollY + 300);
      setSelectedCategoryId(catalog[(idx >= 0 ? idx : catalog.length) - 1].id);
    };

    window.addEventListener('scroll', handleScroll, false);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [catalog, refs]);

  useEffect(() => {
    handleSelectedCategory(catalog?.findIndex(c => c.CatName === categoryName));
  }, [catalog, categoryName, handleSelectedCategory]);

  if (isEmpty(catalog) || loading) return <Loader />;

  const header = `Start Your ${isBulk ? 'Custom Swag' : 'Swag Pack'}`;

  const subHeader = `Get started on designing your custom swag ${
    isBulk ? '' : 'pack'
  } from our selection of curated picks in the
   ${isBulk ? 'hottest swag' : 'most popular swag'} categories
        today. ${
          isBulk
            ? "With individual items, you can skip the packaging (the 'pack') and just focus on the items you love."
            : ''
        } Choose from quality brands including North Face, Stickermule, Moleskine, and more. Then, add
        your swag project details (no credit card info required) to get your mockups!`;

  return (
    <div className={classes.root}>
      <CategoriesSidebar
        catalog={catalog}
        activeCategoryId={selectedCategoryId}
        onSelectedCategory={handleSelectedCategory}
      />
      <Typography className={classes.topCatalogHeader}>{header}</Typography>
      <Typography className={classes.subHeader}> {subHeader}</Typography>
      {categories}
      <div className={classes.nextBtnContainer}>
        {nextStep && (
          <Button
            variant="primary"
            loading={cartLoading}
            disabled={cartLoading}
            component={Link}
            to={nextStep}
            className={classes.nextBtn}
            fullWidth
          >
            Next: Add project details
          </Button>
        )}
      </div>
    </div>
  );
};

export default Catalog;
